import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Clock, Target, Lightbulb, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { analyticsAPI } from '../services/api';

const COLORS = ['#60a5fa', '#34d399', '#fbbf24', '#f87171', '#a78bfa'];

const CHART_TOOLTIP = {
  contentStyle: {
    backgroundColor: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#e2e8f0',
  },
  labelStyle: { color: '#cbd5e1' },
  itemStyle: { color: '#e2e8f0' },
};

const axisProps = {
  stroke: '#475569',
  tick: { fill: '#94a3b8', fontSize: 11 },
};

function PieSliceLabel(props) {
  const { name, count, percent, cx, cy, midAngle, innerRadius, outerRadius } = props;
  if (percent == null || cx == null || midAngle == null) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#cbd5e1"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      style={{ fontSize: 11 }}
    >
      {`${name} ${count} (${(percent * 100).toFixed(0)}%)`}
    </text>
  );
}

function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllAnalytics();
  }, []);

  const fetchAllAnalytics = async () => {
    try {
      const [overviewResponse, performanceResponse, insightsResponse] = await Promise.all([
        analyticsAPI.getOverview(),
        fetch('https://12345backbot6789.aminwarind.cloud/api/analytics/performance').then(res => res.json()),
        fetch('https://12345backbot6789.aminwarind.cloud/api/analytics/insights').then(res => res.json())
      ]);
      
      setAnalytics(overviewResponse.data);
      setPerformanceData(performanceResponse);
      setInsights(insightsResponse);
      setError(null);
    } catch (err) {
      setError('Failed to fetch analytics');
      console.error('Error fetching analytics:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get insight icon
  const getInsightIcon = (type) => {
    switch (type) {
      case 'positive': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info': return <Info className="w-5 h-5 text-blue-500" />;
      default: return <Lightbulb className="w-5 h-5 text-slate-500" />;
    }
  };

  // Helper function to get recommendation priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-slate-800 text-slate-200';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-slate-100">Error</h3>
        <p className="mt-1 text-sm text-slate-500">{error}</p>
        <button
          onClick={fetchAllAnalytics}
          className="mt-4 btn btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100">Analytics & Insights</h1>
        <div className="text-sm text-slate-500">
          Last updated: {new Date().toLocaleTimeString()}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Total Projects</p>
              <p className="text-3xl font-bold text-slate-100">
                {analytics?.total_projects || 0}
              </p>
              <div className="flex items-center mt-2 text-success-400">
                <TrendingUp className="w-4 h-4 mr-1" />
                <span className="text-sm font-medium">+12%</span>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-primary-900/40 border border-primary-800/40">
              <Target className="w-6 h-6 text-primary-400" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Total Bids</p>
              <p className="text-3xl font-bold text-slate-100">
                {analytics?.total_bids || 0}
              </p>
              <div className="flex items-center mt-2 text-success-400">
                <TrendingUp className="w-4 h-4 mr-1" />
                <span className="text-sm font-medium">+8%</span>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-success-900/40 border border-success-800/40">
              <DollarSign className="w-6 h-6 text-success-400" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Success Rate</p>
              <p className="text-3xl font-bold text-slate-100">
                {analytics?.success_rate || 0}%
              </p>
              <div className="flex items-center mt-2 text-danger-600">
                <TrendingDown className="w-4 h-4 mr-1" />
                <span className="text-sm font-medium">-2%</span>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-warning-900/40 border border-warning-800/40">
              <TrendingUp className="w-6 h-6 text-warning-400" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Active Sessions</p>
              <p className="text-3xl font-bold text-slate-100">
                {analytics?.recent_sessions?.filter(s => s.status === 'running').length || 0}
              </p>
              <div className="flex items-center mt-2 text-slate-500">
                <Clock className="w-4 h-4 mr-1" />
                <span className="text-sm font-medium">Current</span>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-danger-100">
              <Clock className="w-6 h-6 text-danger-600" />
            </div>
          </div>
        </div>
      </div>

      {/* AI Insights and Recommendations */}
      {insights && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Insights */}
          <div className="card">
            <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2 text-yellow-500" />
              AI Insights
            </h3>
            <div className="space-y-4">
              {insights.insights && insights.insights.length > 0 ? (
                insights.insights.map((insight, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-slate-800/60 rounded-lg">
                    {getInsightIcon(insight.type)}
                    <div>
                      <h4 className="font-medium text-slate-100">{insight.title}</h4>
                      <p className="text-sm text-slate-400 mt-1">{insight.description}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-4">No insights available yet. Run some bot sessions to generate insights!</p>
              )}
            </div>
          </div>

          {/* Recommendations */}
          <div className="card">
            <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center">
              <Target className="w-5 h-5 mr-2 text-blue-500" />
              Recommendations
            </h3>
            <div className="space-y-4">
              {insights.recommendations && insights.recommendations.length > 0 ? (
                insights.recommendations.map((rec, index) => (
                  <div key={index} className="p-3 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-100">{rec.title}</h4>
                        <p className="text-sm text-slate-400 mt-1">{rec.description}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(rec.priority)}`}>
                        {rec.priority}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-4">No recommendations available yet. Run some bot sessions to get personalized recommendations!</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Bid Trend Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold text-slate-100 mb-4">Daily Bid Trends (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={performanceData?.daily_trends || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" tickFormatter={(value) => new Date(value).toLocaleDateString()} {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip
                {...CHART_TOOLTIP}
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
                formatter={(value, name) => [value, name === 'bids' ? 'Bids' : 'Projects']}
              />
              <Line type="monotone" dataKey="bids" stroke="#60a5fa" strokeWidth={2} name="bids" />
              <Line type="monotone" dataKey="projects" stroke="#34d399" strokeWidth={2} name="projects" />
            </LineChart>
          </ResponsiveContainer>
          {(!performanceData?.daily_trends || performanceData.daily_trends.length === 0) && (
            <p className="text-slate-500 text-center py-8">No trend data available yet</p>
          )}
        </div>

        {/* Project Type Distribution */}
        <div className="card">
          <h3 className="text-lg font-semibold text-slate-100 mb-4">Project Type Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={performanceData?.project_types || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={PieSliceLabel}
                outerRadius={80}
                fill="#6366f1"
                dataKey="count"
                style={{ outline: 'none' }}
              >
                {(performanceData?.project_types || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip {...CHART_TOOLTIP} formatter={(value, name) => [value, 'Projects']} />
            </PieChart>
          </ResponsiveContainer>
          {(!performanceData?.project_types || performanceData.project_types.length === 0) && (
            <p className="text-slate-500 text-center py-8">No project type data available yet</p>
          )}
        </div>
      </div>

      {/* Additional Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Currency Distribution */}
        <div className="card">
          <h3 className="text-lg font-semibold text-slate-100 mb-4">Currency Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={performanceData?.currency_distribution || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="name" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip {...CHART_TOOLTIP} />
              <Bar dataKey="count" fill="#a78bfa" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          {(!performanceData?.currency_distribution || performanceData.currency_distribution.length === 0) && (
            <p className="text-slate-500 text-center py-8">No currency data available yet</p>
          )}
        </div>

        {/* Budget Range Distribution */}
        <div className="card">
          <h3 className="text-lg font-semibold text-slate-100 mb-4">Budget Range Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={performanceData?.budget_ranges || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="name" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip {...CHART_TOOLTIP} />
              <Bar dataKey="count" fill="#fbbf24" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          {(!performanceData?.budget_ranges || performanceData.budget_ranges.length === 0) && (
            <p className="text-slate-500 text-center py-8">No budget data available yet</p>
          )}
        </div>
      </div>

      {/* Session Performance */}
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">Session Performance</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-800/60">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Session ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Start Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Bids Placed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Projects Found
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Efficiency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Errors
                </th>
              </tr>
            </thead>
            <tbody className="bg-slate-900/50 divide-y divide-slate-700">
              {performanceData?.session_performance?.slice(0, 10).map((session, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-100">
                    {session.session_id?.slice(0, 8)}...
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-100">
                    {session.start_time ? new Date(session.start_time).toLocaleString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`status-badge ${
                      session.status === 'running' ? 'status-running' : 
                      session.status === 'stopped' ? 'status-stopped' : 'status-error'
                    }`}>
                      {session.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-100">
                    {session.total_bids_placed || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-100">
                    {session.unique_projects_bid_on || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-100">
                    {session.efficiency}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-100">
                    {session.total_errors || 0}
                  </td>
                </tr>
              )) || (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-sm text-slate-500">
                    No session performance data available yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}

export default Analytics;

